import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/header/header';
import Footer from '../../components/footer/footer';
import PageMeta from '../../components/pageMeta/PageMeta';
import { trackEvent } from '../../utils/analytics';
import './home.css';

const workflows = {
  operations: {
    label: 'Operations agent',
    prompt: 'A new partner onboarding request arrives.',
    steps: [
      ['01', 'Trigger', 'Intake received from the website'],
      ['02', 'Context', 'Company data and policy checks retrieved'],
      ['03', 'Action', 'CRM record and review packet prepared'],
      ['04', 'Approval', 'Founder reviews flagged exceptions'],
      ['05', 'Result', 'Approved records move to onboarding'],
    ],
    result: 'A repeatable workflow with exceptions routed to a human.',
  },
  research: {
    label: 'Research agent',
    prompt: 'The founder requests a competitor brief.',
    steps: [
      ['01', 'Scope', 'Research questions converted into a plan'],
      ['02', 'Retrieve', 'Approved sources collected and cited'],
      ['03', 'Verify', 'Conflicting evidence highlighted'],
      ['04', 'Synthesize', 'Findings structured around the decision'],
      ['05', 'Result', 'Review-ready brief delivered with citations'],
    ],
    result: 'A useful decision artifact, not an untraceable wall of text.',
  },
  support: {
    label: 'Support agent',
    prompt: 'A customer reports an account access problem.',
    steps: [
      ['01', 'Triage', 'Intent and urgency classified'],
      ['02', 'Context', 'Account and knowledge base checked'],
      ['03', 'Draft', 'Grounded response and action prepared'],
      ['04', 'Escalate', 'Sensitive account change held for approval'],
      ['05', 'Result', 'Customer receives a fast, controlled response'],
    ],
    result: 'Automation where it is safe; human judgment where it matters.',
  },
};

const capabilities = [
  {
    number: '01',
    title: 'AI agents',
    copy: 'Purpose-built agents that reason, use tools, and complete bounded workflows across your product or operations.',
    examples: ['Task agents', 'Multi-agent systems', 'MCP integrations'],
  },
  {
    number: '02',
    title: 'Knowledge systems',
    copy: 'RAG and retrieval systems that turn fragmented company knowledge into grounded, traceable answers.',
    examples: ['Internal copilots', 'Semantic search', 'Citation pipelines'],
  },
  {
    number: '03',
    title: 'Workflow automation',
    copy: 'Event-driven automations that connect your tools, apply business logic, and route exceptions intelligently.',
    examples: ['Operations', 'Sales workflows', 'Back-office automation'],
  },
  {
    number: '04',
    title: 'Voice and conversation',
    copy: 'Low-latency voice and chat experiences designed around real tasks, escalation, and measurable service quality.',
    examples: ['Voice agents', 'Product copilots', 'Support interfaces'],
  },
];

const representativeWork = [
  {
    type: 'Representative system / operations',
    title: 'Document review that knows when to stop',
    copy: 'A retrieval and review pipeline that extracts terms, checks policy, records citations, and sends uncertain cases to a human.',
    flow: 'Documents → retrieval → evaluation → approval',
  },
  {
    type: 'Representative system / research',
    title: 'Decision briefs with traceable evidence',
    copy: 'A multi-step research agent that plans the work, gathers approved sources, compares evidence, and produces reviewable outputs.',
    flow: 'Question → sources → verification → brief',
  },
  {
    type: 'Representative system / support',
    title: 'Customer support with controlled actions',
    copy: 'A conversational agent that retrieves account context, drafts grounded responses, and escalates sensitive changes.',
    flow: 'Message → context → response → escalation',
  },
];

const process = [
  ['01', 'Discover', 'Choose one valuable workflow, map the edge cases, and define what success means.'],
  ['02', 'Prototype', 'Build the thinnest useful system against representative data and real integrations.'],
  ['03', 'Evaluate', 'Test quality, cost, latency, safety, and failure modes before expanding the scope.'],
  ['04', 'Deploy', 'Ship with observability, human approvals, fallbacks, and ownership documentation.'],
  ['05', 'Improve', 'Use production signals to tune prompts, retrieval, tools, and evaluation suites.'],
];

const faqs = [
  ['Where should an early-stage startup begin?', 'Start with one workflow that is frequent, painful, and easy to measure. We help you narrow the scope before writing production code.'],
  ['Can you work with our existing product and stack?', 'Yes. Most useful agent systems connect to existing data, APIs, and workflows rather than replacing them.'],
  ['How do you keep agents reliable?', 'We combine bounded tools, structured outputs, evaluations, observability, fallbacks, and human checkpoints based on the risk of each action.'],
  ['Do you only build prototypes?', 'No. Prototypes are used to reduce uncertainty. The engagement is designed around deployment, monitoring, and a clear path to ownership.'],
];

const WorkflowLab = () => {
  const [active, setActive] = useState('operations');
  const workflow = workflows[active];

  return (
    <div className="workflow-lab">
      <div className="workflow-lab__tabs" role="tablist" aria-label="Example agent workflows">
        {Object.entries(workflows).map(([key, item]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={active === key}
            className={active === key ? 'is-active' : ''}
            onClick={() => {
              setActive(key);
              trackEvent('workflow_demo_selected', { workflow: key });
            }}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className="workflow-lab__body" role="tabpanel">
        <div className="workflow-lab__request">
          <span>Incoming request</span>
          <strong>{workflow.prompt}</strong>
        </div>
        <ol className="workflow-lab__steps">
          {workflow.steps.map(([number, label, copy]) => (
            <li key={number}>
              <span className="workflow-lab__number">{number}</span>
              <span><small>{label}</small><strong>{copy}</strong></span>
              <i aria-hidden="true" />
            </li>
          ))}
        </ol>
        <div className="workflow-lab__result">
          <span aria-hidden="true">✓</span>
          <p><small>Controlled outcome</small><strong>{workflow.result}</strong></p>
        </div>
      </div>
    </div>
  );
};

const Home = () => (
  <div className="home-page">
    <PageMeta
      title="TryOuts | Production AI Agents for Startups"
      description="TryOuts designs, evaluates, and deploys production AI agents, RAG systems, workflow automation, and voice interfaces for startup teams."
      path="/"
    />
    <Header />
    <main id="main-content">
      <section className="hero section--night">
        <div className="hero__grid container">
          <div className="hero__copy">
            <p className="eyebrow">AI systems studio / India → global</p>
            <h1>AI agents that do the work—not just demo it.</h1>
            <p className="hero__intro">We help startup founders turn valuable workflows into production AI systems that connect to real tools, handle edge cases, and stay observable after launch.</p>
            <div className="hero__actions">
              <Link className="button button--primary" to="/contact" onClick={() => trackEvent('primary_cta_clicked', { location: 'hero' })}>
                Plan an agent build <span aria-hidden="true">↗</span>
              </Link>
              <a className="button button--secondary" href="#work">See systems in action</a>
            </div>
            <p className="hero__note"><span /> Strategy, engineering, evaluation, and deployment—one senior team.</p>
          </div>

          <div className="system-map" aria-label="Example AI agent system map">
            <div className="system-map__top">
              <span>System map / live architecture</span>
              <span className="system-map__status"><i /> Observable</span>
            </div>
            <div className="system-map__canvas">
              <div className="system-node system-node--trigger"><small>01 / trigger</small><strong>New request</strong><span>Webhook received</span></div>
              <div className="system-node system-node--context"><small>02 / context</small><strong>Retrieve</strong><span>CRM · docs · policies</span></div>
              <div className="system-node system-node--agent"><small>03 / reason</small><strong>Agent</strong><span>Plan · use tools · verify</span></div>
              <div className="system-node system-node--human"><small>04 / control</small><strong>Human review</strong><span>Exception flagged</span></div>
              <div className="system-node system-node--result"><small>05 / result</small><strong>Action completed</strong><span>Audit trail saved</span></div>
              <svg viewBox="0 0 620 420" aria-hidden="true" preserveAspectRatio="none">
                <path d="M128 90 C220 90 190 176 294 176" />
                <path d="M128 300 C220 300 195 210 294 210" />
                <path d="M385 194 C480 194 440 95 520 95" />
                <path d="M385 214 C480 214 450 310 520 310" />
              </svg>
            </div>
          </div>
        </div>
      </section>

      <section className="principles" aria-label="Engineering principles">
        <div className="container principles__grid">
          <div><span>01</span><strong>Measured before release</strong><p>Evaluation is part of the build, not a final checkbox.</p></div>
          <div><span>02</span><strong>Humans at the right checkpoints</strong><p>High-risk actions stay reviewable and reversible.</p></div>
          <div><span>03</span><strong>Observable after launch</strong><p>Failures, cost, latency, and quality remain visible.</p></div>
        </div>
      </section>

      <section className="section section--paper" id="work">
        <div className="container">
          <p className="eyebrow">Agents in action</p>
          <h2 className="section-title">Make the invisible workflow visible.</h2>
          <p className="section-intro">A useful agent is more than a chat box. Explore how a controlled system moves from an incoming request to a verifiable result.</p>
          <WorkflowLab />
        </div>
      </section>

      <section className="section section--night founder-problems">
        <div className="container">
          <p className="eyebrow">Built around the bottleneck</p>
          <h2 className="section-title">Start with the workflow your team should not have to repeat.</h2>
          <div className="problem-grid">
            <article><span>01</span><h3>Automate an operation</h3><p>Connect fragmented tools and turn repetitive, decision-heavy work into an observable system.</p></article>
            <article><span>02</span><h3>Add intelligence to a product</h3><p>Build an agentic feature with real context, controlled tool use, and a reliable user experience.</p></article>
            <article><span>03</span><h3>Move a prototype to production</h3><p>Replace the happy-path demo with evaluations, fallbacks, monitoring, and deployment architecture.</p></article>
          </div>
        </div>
      </section>

      <section className="section section--paper" id="capabilities">
        <div className="container">
          <p className="eyebrow">Capabilities</p>
          <h2 className="section-title">The full system, not another wrapper.</h2>
          <p className="section-intro">We work from the user experience down through orchestration, retrieval, integrations, evaluations, and deployment.</p>
          <div className="capability-grid">
            {capabilities.map((item) => (
              <article className="capability-card" key={item.number}>
                <span>{item.number}</span>
                <h3>{item.title}</h3>
                <p>{item.copy}</p>
                <ul>{item.examples.map((example) => <li key={example}>{example}</li>)}</ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section showcase">
        <div className="container">
          <div className="showcase__heading">
            <div><p className="eyebrow">Representative work</p><h2 className="section-title">Systems designed for real operating conditions.</h2></div>
            <p>These examples illustrate how we structure common agent systems. They are not presented as client results.</p>
          </div>
          <div className="work-grid">
            {representativeWork.map((item, index) => (
              <article className="work-card" key={item.title}>
                <div className="work-card__visual" aria-hidden="true"><span>0{index + 1}</span><i /><i /><i /><i /></div>
                <p className="work-card__type">{item.type}</p>
                <h3>{item.title}</h3>
                <p>{item.copy}</p>
                <code>{item.flow}</code>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--night architecture">
        <div className="container architecture__grid">
          <div>
            <p className="eyebrow">Production architecture</p>
            <h2 className="section-title">Every layer has a job. Every action leaves a trace.</h2>
            <p className="section-intro">We separate context, reasoning, tool use, control, and evaluation so the system can be tested and improved without guesswork.</p>
          </div>
          <div className="architecture-stack">
            <div><span>Experience</span><strong>Voice · chat · product UI · background jobs</strong></div>
            <div><span>Orchestration</span><strong>State · routing · memory · retries</strong></div>
            <div><span>Intelligence</span><strong>Models · retrieval · prompts · tools</strong></div>
            <div><span>Control</span><strong>Evaluations · approvals · guardrails · logs</strong></div>
            <div><span>Infrastructure</span><strong>APIs · queues · databases · deployment</strong></div>
          </div>
        </div>
      </section>

      <section className="section section--paper" id="process">
        <div className="container">
          <p className="eyebrow">How we work</p>
          <h2 className="section-title">From uncertain idea to owned system.</h2>
          <div className="process-list">
            {process.map(([number, title, copy]) => (
              <article key={number}><span>{number}</span><h3>{title}</h3><p>{copy}</p></article>
            ))}
          </div>
        </div>
      </section>

      <section className="section reliability">
        <div className="container">
          <div className="reliability__heading">
            <div><p className="eyebrow">Reliability by design</p><h2 className="section-title">Autonomous does not mean unsupervised.</h2></div>
            <p>We choose control based on consequence. The more sensitive the action, the stronger the approval, validation, and audit path.</p>
          </div>
          <div className="reliability-grid">
            <article><span>✓</span><h3>Evaluation suites</h3><p>Representative test cases make quality changes measurable before release.</p></article>
            <article><span>↗</span><h3>Human escalation</h3><p>Uncertain and high-impact decisions move cleanly to the right person.</p></article>
            <article><span>◎</span><h3>Observability</h3><p>Tool calls, latency, costs, failures, and outcomes stay inspectable.</p></article>
            <article><span>↺</span><h3>Fallbacks</h3><p>Retries, validation, safe defaults, and reversible actions protect the workflow.</p></article>
          </div>
        </div>
      </section>

      <section className="section section--paper about" id="about">
        <div className="container about__grid">
          <div><p className="eyebrow">About TryOuts</p><h2 className="section-title">A direct engineering partner for founders.</h2></div>
          <div className="about__copy">
            <p>TryOuts is an India-based AI systems studio working with startup teams globally. We focus on the difficult middle between a compelling model demo and software people can depend on.</p>
            <p>You work directly with the people shaping the architecture and shipping the system—without a handoff maze between sales, strategy, and engineering.</p>
            <Link className="text-link" to="/contact">Tell us where the workflow breaks <span aria-hidden="true">↗</span></Link>
          </div>
        </div>
      </section>

      <section className="section stack-section section--night">
        <div className="container">
          <p className="eyebrow">Selected stack</p>
          <h2 className="section-title">Technology chosen for the system—not the slide.</h2>
          <div className="stack-grid">
            <div><span>Models</span><p>OpenAI · Anthropic · open models</p></div>
            <div><span>Orchestration</span><p>LangGraph · Agents SDK · custom state machines</p></div>
            <div><span>Retrieval</span><p>Postgres · Pinecone · Weaviate</p></div>
            <div><span>Voice</span><p>ElevenLabs · Twilio · real-time APIs</p></div>
            <div><span>Automation</span><p>n8n · Make · product APIs</p></div>
            <div><span>Deployment</span><p>FastAPI · Node.js · cloud infrastructure</p></div>
          </div>
        </div>
      </section>

      <section className="section labs section--paper" id="labs">
        <div className="container labs__card">
          <div><p className="eyebrow">TryOuts Labs / GenL</p><h2>We build our own products, too.</h2><p>GenL is our lead-generation product experiment—a place to test the same product, automation, and growth systems we build with founders.</p></div>
          <a className="button button--dark" href="https://genl.tryouts.tech/" target="_blank" rel="noreferrer">Explore GenL <span aria-hidden="true">↗</span></a>
        </div>
      </section>

      <section className="section faq section--paper">
        <div className="container faq__grid">
          <div><p className="eyebrow">Founder FAQ</p><h2 className="section-title">Before we build.</h2></div>
          <div className="faq__list">
            {faqs.map(([question, answer]) => <details key={question}><summary>{question}<span aria-hidden="true">+</span></summary><p>{answer}</p></details>)}
          </div>
        </div>
      </section>

      <section className="final-cta section--night">
        <div className="container final-cta__inner">
          <p className="eyebrow">Have a workflow in mind?</p>
          <h2>Bring us the bottleneck.<br />We’ll map the system.</h2>
          <Link className="button button--primary" to="/contact" onClick={() => trackEvent('primary_cta_clicked', { location: 'footer_cta' })}>Plan an agent build <span aria-hidden="true">↗</span></Link>
        </div>
      </section>
    </main>
    <Footer />
  </div>
);

export default Home;
